<template>
  <BlokkliProvider v-bind="blokkli">
    <Container>
      <div class="max-w-prose">
        <ContentHeader :title="$texts('cart.title', 'Warenkorb')" />
      </div>
    </Container>

    <Container>
      <div class="box box-padding bg-white">
        <div v-if="cartIsEmpty" class="flex flex-col items-center">
          <SpriteSymbol name="cart" class="text-gray-300 h-80 w-80" />
          <div class="text-xl lg:text-2xl mt-20 mb-40">
            {{ $texts('cartIsEmpty', 'Ihr Warenkorb ist leer.') }}
          </div>
          <ButtonStyle v-slot="{ classes }">
            <NuxtLink :to="{ name: 'shop' }" :class="classes">
              {{ $texts('cartDiscoverShop', 'Produkte entdecken') }}
            </NuxtLink>
          </ButtonStyle>
        </div>
        <template v-else>
          <table class="cart-table">
            <thead
              class="text-sm uppercase font-medium text-left border-b border-b-gray-300"
            >
              <tr>
                <th colspan="2">
                  {{ $texts('cart.tableHeaderProduct', 'Produkt') }}
                </th>
                <th colspan="2">
                  {{ $texts('cart.tableHeaderQuantity', 'Menge') }}
                </th>
                <th class="text-right pr-10">
                  {{ $texts('cart.tableHeaderPriceUnit', 'Preis CHF') }}
                </th>
                <th class="text-right">
                  {{ $texts('cart.tableHeaderTotalPrice', 'Total CHF') }}
                </th>
              </tr>
            </thead>
            <tbody>
              <CommerceOrderItem
                v-for="item in cart.orderItems"
                :key="item.id"
                v-bind="item"
                @remove="cart.removeOrderItem"
                @change-quantity="
                  cart.changeQuantity($event.id, $event.quantity)
                "
              />
            </tbody>

            <tfoot class="border-y border-y-gray-300 text-base">
              <tr>
                <td colspan="2">
                  {{ $texts('cart.subtotal', 'Zwischensumme') }}
                </td>
                <td colspan="4" class="text-right">{{ cart.subtotal }}</td>
              </tr>
              <tr v-for="adjustment in summaryAdjustments">
                <td colspan="2">
                  <span>{{ adjustment.label }}</span>
                  <span
                    v-if="adjustment.suffix"
                    class="font-medium inline-block ml-3"
                    >{{ adjustment.suffix }}</span
                  >
                </td>
                <td colspan="4" class="text-right">
                  {{ adjustment.value }}
                </td>
              </tr>
              <tr class="md:text-lg font-semibold">
                <td colspan="2">
                  {{ $texts('cart.totalWithTax', 'Total mit MwSt.') }}
                </td>
                <td colspan="4" class="text-right">{{ cart.total }}</td>
              </tr>
            </tfoot>
          </table>

          <div class="flex justify-end pt-10 md:pt-30">
            <ButtonStyle v-slot="{ classes }">
              <a
                :href="checkoutUrl"
                :class="[classes, { 'is-loading': isRedirecting }]"
                @click="isRedirecting = true"
              >
                <span>{{ $texts('cart.goToCheckout', 'Zur Kasse') }}</span>
                <SpriteSymbol name="arrow" class="w-20 h-10 ml-10" />
              </a>
            </ButtonStyle>
          </div>
        </template>
      </div>
      <BlokkliField
        v-if="node"
        :list="node.paragraphs"
        non-empty-class="mt-40"
        name="field_paragraphs"
      />
    </Container>
  </BlokkliProvider>
</template>

<script lang="ts" setup>
import { TAX_REDUCED } from '@pharmasuisse/helpers/commerce'
import { useCartStore } from '~/stores/cart'
import type { NodePageFragment } from '#graphql-operations'

await initFormKit()

definePageMeta({
  name: 'cart',
  staticDrupalPage: 'pharmasuisse_cart',
  drupalRoute: true,
})

const language = useCurrentLanguage()
const nuxtRoute = useRoute()

// Get the data.
const { data: query } = await useAsyncData(nuxtRoute.path, () =>
  useCachedGraphqlQuery('route', {
    path: nuxtRoute.path,
  }).then((v) => v.data),
)

const { entity: node } = await useDrupalRoute<NodePageFragment>(query.value, {
  noError: true,
})
setBreadcrumbLinksFromRoute(query.value)

const cart = useCartStore()

const isRedirecting = ref(false)

const cartIsEmpty = computed(() => {
  return !cart.orderItems.length
})

const checkoutUrl = computed(() => {
  return `/${language.value}/checkout`
})

const summaryAdjustments = computed(() => {
  return cart.adjustments.map((v) => {
    const isReducedTax = v.sourceId === TAX_REDUCED
    return {
      label: v.label,
      suffix: isReducedTax ? '*' : '',
      value: v.amount?.formatted || '',
    }
  })
})

setLanguageLinksFromRoute(query.value)
useRouteCache((c) => c.setUncacheable())
await renderPageDependencies()
const blokkli = buildBlokkliProps(node, 'node', 'page')
</script>

<script lang="ts">
export default {
  name: 'PagePharmasuisseCart',
}
</script>

<style lang="postcss">
.cart-table {
  @apply w-full;

  thead {
    @apply mobile-only:hidden;
    th {
      @apply pb-10 font-medium;
    }
  }

  tbody {
    tr {
      @apply mobile-only:flex mobile-only:py-10 mobile-only:items-center mobile-only:flex-wrap not-last:border-b not-last:border-b-gray-300;
    }
    td {
      @apply mobile-only:block mobile-only:first:flex-1 md:py-20 md:not-last:pr-10;
    }
  }

  tfoot {
    tr {
      @apply mobile-only:flex mobile-only:justify-between;
    }
    td {
      @apply py-10;
    }
    tr:last-child {
      td {
        @apply md:pb-25 pt-5;
      }
    }
  }
}
</style>
